import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { IconButton, Paper, Skeleton, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useHistory, useLocation, useParams } from "react-router";
import Swal from "sweetalert2";
import AlterImage from '../../../assets/images/alter_image.png';
import DeleteItemIcon from "../../../assets/images/icons/DeleteItem.png";
import PencilSimpleRoundedOutlineIcon from '../../../assets/images/icons/PencilSimpleRoundedOutline.svg';
import TrashRoundedOutlineIcon from '../../../assets/images/icons/TrashRoundedOutline.svg';
import { Service } from "../../../Service/ApiService";
import { CommonService } from "../../../Service/CommonService";
import Spinner from "../../Common/Spinner";
import useFormValidation from "../../Dynamic/FormValidation";
import ImageUploader from "../../Dynamic/ImageUploader";
import ChangePassword from "../ChangePassword";
import './ViewOrEditProfile.scss';
import { useZipcodeValidation } from "../../../hooks/useZipcodeValidation";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import DataGridLoader from "../../Common/DataGridLoader";

const ViewOrEditProfile = () => {
    const { id } = useParams<{ id: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const [isValidZipcode, setValidZipcode] = useState(false);
    const [isZipcodeLoading, setZipcodeLoading] = useState(false);
    const dateInputRef = useRef(null);
    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);
    const handleClose = () => { setShow(false); };
    const [isEditDetail, setEditDetail] = useState(false);
    const handleEditDetail = () => { setEditDetail(true); setChangePassword(false); setIsRecentLoginActivity(false);}
    const handleViewDetail = () => { setEditDetail(false); };
    const [isChangePassword, setChangePassword] = useState(false);
    const showChangePassword = () => { setChangePassword(true); setEditDetail(false); setIsRecentLoginActivity(false);}
    const hideChangePassword = () => setChangePassword(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const imageFormData = new FormData();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const location = useLocation();
    const currentPath = location.pathname;
    const history = useHistory();
    const [recentLoginActivityData, setRecentLoginActivityData] = useState<any>({});
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [tableData, setTableData] = useState<any[]>([]);
    const [isRecentLoginActivity, setIsRecentLoginActivity] = useState(false);
    const [activeTab, setActiveTab] = useState("edit");

    let ImageDetail = {};
    const initialFormData = {
        firstName: "",
        lastName: "",
        birthdate: "",
        zipcode: "",
        phoneNumber: "",
        email: "",
        password: "",
        retypePassword: "",
        companyName: "",
        companyEmail: "",
        contactNumber: "",
        contactPerson: "",
        companyId: "",
        siteId: id,
        roleId: "",
        userStatus: "ACCEPTED",
        city: "",
        state: "",
        streetAddress: "",
        isBroker: false, 
    };

    const { formData, errors, handleChange, handleBlur, setErrors, validateForm, setFormData } = useFormValidation(initialFormData);
    
    // integrate zipcode validation with the form states
    const { zipcodeLoading, validZipcode, validatePostalCode } =
    useZipcodeValidation(setFormData, setErrors);

    const handleDateClick = () => {
        if (dateInputRef.current) {
            dateInputRef.current?.showPicker(); // This will trigger the date picker to open
        }
    };

    const handleIsBrokerChange = (event) => {
        setFormData(prevData => ({
          ...prevData,
          isBroker: event.target.checked
        }));
      };

    const getMinAgeDate = (): string => {
        const today = new Date();
        const minAgeYear = today.getFullYear() - 21;
        return `${minAgeYear}-${String(today.getMonth() + 1).padStart(
            2,
            "0"
        )}-${String(today.getDate()).padStart(2, "0")}`;
    };

    const minAgeDate = getMinAgeDate();

    const validateZipcode = async (zipcode: string): Promise<boolean> => {
        return await validatePostalCode(zipcode);
    }

    const handleZipcodeBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        const zipcode = e.target.value;
        await validateZipcode(zipcode);
    };

    const fetchUserById = async () => {
        const response: any = await Service.getUserById(id);
        setFormData(response.data);
    };

    // Validate user fields
    const validateUserFields = async (): Promise<boolean> => {
        let isValidationPass = true;

        const requiredFields = [
            { field: "firstName", error: "Please enter first name" },
            { field: "birthdate", error: "Please enter birthdate" },
            { field: "email", error: "Please enter email" },
            { field: "companyName", error: "Please enter company name" },
        ];

        requiredFields.forEach(({ field, error }) => {
            if (formData[field]?.trim().length === 0 || errors[field]) {
                if (formData[field]?.trim().length === 0) {
                    setErrors((prevState) => ({ ...prevState, [field]: error }));
                }
                isValidationPass = false;
            }
        });

        if (formData?.zipcode?.length === 0 || errors?.zipcode) {
            if (formData?.zipcode?.length === 0) {
                setErrors((prevState) => ({
                    ...prevState,
                    zipcode: "Please enter zipcode",
                }));
            }
            isValidationPass = false;
        } else {
            const result = await validateZipcode(formData?.zipcode?.trim());
            if(!result) {
                isValidationPass = false;
            }
        }

        return isValidationPass;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); // Prevent the default form submission behavior
        setIsLoading(true);

        const isFormValid = await validateUserFields();

        if (!isFormValid) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await Service.updateUser(formData);
            if (response.status === 200) {
                CommonService.Toast.fire({
                    title: "User Updated Successfully",
                    icon: "success",
                });
            }
            const { firstName, lastName } = response.data as any;
            const fullName = lastName ? `${firstName} ${lastName}`.trim() : firstName;
            localStorage.setItem("updateUserName", fullName);
            localStorage.setItem("userName", fullName);
        } catch (error: any) {
            CommonService.Toast.fire({
                title: error.response.data.errorMessage,
                icon: "error",
            });
        } finally {
            fetchUserById();
            handleViewDetail();
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserById();
        getProfileImage();
    }, [id]);

    // Convert base64 to a file
    const base64ToFile = (base64: string, filename: string): void => {
        if (base64 && filename) {
            const arr = base64.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || '';
            const bstr = atob(arr[1]);
            const u8arr = new Uint8Array(bstr.length);

            for (let i = 0; i < bstr.length; i++) {
                u8arr[i] = bstr.charCodeAt(i);
            }

            const file = new File([u8arr], filename, { type: mime });

            // Create form data to send to the backend
            imageFormData.append('image', file);
           
            ImageDetail = {
                id: id,
                fileName: filename,
            };

            handleSaveImage();
        }else{
            setSelectedFile(null);
        }
        handleClose();
    };

    const handleSaveImage = async () => {
        try {
            setIsLoading(true);

            imageFormData.append(
                "param",
                new Blob([JSON.stringify(ImageDetail)], {
                    type: "application/json",
                })
            );

            const response = await Service.uploadProfileImage(imageFormData);
            if (response.status === 200) {
                CommonService.Toast.fire({
                    title: "Profile picture updated successfully",
                    icon: "success",
                });
            }else{
                CommonService.Toast.fire({
                    title: "There is some technical problem occured",
                    icon: "error",
                }); 
            }
            localStorage.removeItem("profile");
            handleClose();
        } catch (error) {
            console.error('Error occured on profile picture uploading:', error);
        } finally {
            getProfileImage();
            setSelectedFile(null);
            setIsLoading(false);
        }
    };

    // const getProfileImage = async () => {
    //     if (CommonService.getProfile() == null) {
    //         const requestDto = {
    //             id: id
    //         };
    //         try {

    //             const response: any = await Service.getProfileImage(requestDto);
    //             const blob = response.data as Blob;
    //             if (blob.size != 0) {
    //                 // Create a URL for the Blob and set it as the image source
    //                 const imageUrl = URL.createObjectURL(blob);
    //                 setImageSrc(imageUrl);
    //                 localStorage.setItem("profile", imageUrl);

    //                 //Dispatch custom event
    //                 const event = new CustomEvent('profileImageChanged');//, { detail: imageUrl });
    //                 window.dispatchEvent(event);
    //             } else {
    //                 setImageSrc(null);
    //             }
    //         } catch (error) {
    //             console.error("Error occured on fetching profile picture:", error);
    //         }
    //     } else {
    //         setImageSrc(CommonService.getProfile());
    //     }
    // };

    const getProfileImage = async () => {
        
        let isUserDetailsRoute = currentPath.includes('/user-detail');

        if (CommonService.getProfile() == null || isUserDetailsRoute) {
          const requestDto = { id: id };
          try {
            const response: any = await Service.getProfileImage(requestDto);
            const blob = response.data as Blob;
            if (blob.size != 0) {
              // Create a URL for the Blob and set it as the image source
              const imageUrl = URL.createObjectURL(blob);
              setImageSrc(imageUrl);
              
              // Only set in localStorage if not on the user-details route
              if (!isUserDetailsRoute) {
                localStorage.setItem("profile", imageUrl);

                // Dispatch custom event
                const event = new CustomEvent('profileImageChanged');
                window.dispatchEvent(event);
              }
            } else {
              setImageSrc(null);
            }
          } catch (error) {
            console.error("Error occurred on fetching profile picture:", error);
          }
        } else {
          setImageSrc(CommonService.getProfile());
        }
      };

    const removeProfileImage = async () => {

        let isUserDetailsRoute = currentPath.includes('/user-detail');

        const requestDto = {
            id: id,
            fileName: null
        };
        try {

            const response: any = await Service.removeProfileImage(requestDto);
            if (response.status === 200) {
                CommonService.Toast.fire({
                    title: "Profile picture removed successfully ",
                    icon: "success",
                });
                setImageSrc(null);

                if(!isUserDetailsRoute)
                    localStorage.removeItem("profile");

                //Dispatch custom event
                const event = new CustomEvent('profileImageChanged');//, { detail: null });
                window.dispatchEvent(event);
                handleClose();
            }
        } catch (error) {
            console.error("Error occured on profile picture removed:", error);
        } finally {
            getProfileImage();
        }
    };

    const removePicConfirmation = () => {
        Swal.fire({
        html: `
        <style>
            .custom-swal .icon-container {
                background-color: #ffebee;
                border-radius: 50%;
                width: 105px;
                height: 105px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: 0 auto 20px;
            }
            .custom-swal .icon-container i {
                color: #ff3e4e;
                font-size: 24px;
            }
            .custom-swal h2 {
                font-size: 20px;
                margin-bottom: 10px;
                margin-top: 20px;
            }
            .custom-swal p {
                font-size: 14px;
                color: #666;
                margin-bottom: 20px;
            }
            .custom-swal .button-container {
                margin-top: 40px;
                display: flex;
                justify-content: center;
                gap: 20px;
            }
            .custom-swal button {
                border: none;
                padding: 10px 50px;
                border-radius: 30px;
                font-size: 14px;
                cursor: pointer;
            }
            .custom-swal .cancel-btn {
                background-color: #e0e0e0;
                color: #333;
            }
            .custom-swal .delete-btn {
                background-color: #ec2323;
                color: white;
            }
            
        </style>
        <div class="custom-swal">

        <div class="icon-container" id="iconContainer">
            </div>

            <h2>Are you sure you want to remove the profile picture?</h2>
            <p></p>
            <div class="button-container">
            <button class="cancel-btn" id="cancelBtn">Cancel</button>
            <button class="delete-btn" id="deleteBtn">Delete</button>
            </div>
        </div>
        `,
        showConfirmButton: false,
        showCancelButton: false,
        showCloseButton: true,
        focusCancel: true,
        customClass: {
            popup: "custom-popup",
        },
        backdrop: `rgba(0,0,0,0.4)`,
        width: "500px",
        background: "#f8f8f8",
        didOpen: () => {
            // Add the image after the popup has been rendered
            const iconContainer = Swal.getPopup()!.querySelector("#iconContainer");
            if (iconContainer) {
            const img = document.createElement("img");
            img.src = DeleteItemIcon;
            img.width = 47;
            img.height = 51;
            iconContainer.appendChild(img);
            }

            // Add event listeners to the buttons
            const cancelBtn = Swal.getPopup()!.querySelector("#cancelBtn");
            const deleteBtn = Swal.getPopup()!.querySelector("#deleteBtn");

            cancelBtn?.addEventListener("click", () => {
             Swal.close();
            });

            deleteBtn?.addEventListener("click", () => {
                removeProfileImage();
                Swal.close();
            });
        },
        });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files?.[0];
            setSelectedFile(file);

            // Clear the input value to allow selecting the same file again
            event.target.value = '';
        }
    };

    const handleBack = () => {
        history.goBack();
      }
    
    const showRecentLoginActivity = () => {
        setIsRecentLoginActivity(true);
        setEditDetail(false);
        setChangePassword(false);
        getRecentLoginActivityData(0, pageSize);
    };

    const getRecentLoginActivityData = async (page, size) => {
        try {

            setIsLoading(true);
            let userId;
            const isAdmin = CommonService.isAdmin();
            if(isAdmin && CommonService.getCompanyId() === formData.companyId) {
                userId = id;
            } else if(!isAdmin && CommonService.getCompanyId() === formData.companyId) {
                userId = id;
            } else if(isAdmin && CommonService.getCompanyId() !== formData.companyId) {
                userId = formData.id;
            }
            const response = await Service.getRecentLoginActivityData(page, size, userId);

            setTableData(response.data.content);
            setTotalCount(response.data.totalElements);
        } catch (error) {
            console.log("error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()}>
            <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const tableDataWithIds = tableData?.map((rowData, index) => ({
        id: index,
        ...rowData,
    })) || []; 
    
    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "User Name",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "ipAddress",
            headerName: "IP Address",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "userAgent",
            headerName: "User Agent",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "address",
            headerName: "Location",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
        },
        {
            field: "loginTime",
            headerName: "Login Time",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
            valueGetter: (value: any, row: any) => {
                const dateTimeString = row.loginTime ? row.loginTime : null;
                if (dateTimeString) {
                    return CommonService.formatDateTime(dateTimeString);
                }
            },
        },
    ];

    return (
        <>
            <div className={`usersList ${(CommonService.isAdmin() || CommonService.isWarehouse()) ? "profile-admin": "profile-user"}`}>
                <div className="usersList__header--wrapper d-flex flex-wrap  align-items-center">
                {(CommonService.isAdmin() || CommonService.isWarehouse()) && (<IconButton
                  onClick={handleBack}
                  aria-label="ArrowBackIcon"
                  sx={{
                    backgroundColor: "#FFF",
                    "&:hover": { backgroundColor: "#FFF" },
                  }}
                >
                  <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                </IconButton> )}
                    <div className="header--title">
                        <h1 className="title mb-0 ms-2">
                            Profile Details
                        </h1>
                    </div>
                </div>
                <div className="profile-wrapper rounded-2 bg-white">
                    <div className="profile-details mt-4 mb-3">
                        <div className="row">
                            <div className="col-xl-3">
                                <div className="py-5">
                                    <div className="d-flex flex-column justify-content-center align-items-center mb-3">
                                        <span className="profile-avatar position-relative">
                                            <img src={imageSrc || AlterImage} alt="ProfileAvatarIcon" className='img-fluid rounded-circle'
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.onerror = null; // Prevent infinite loop if the fallback image fails
                                                    target.src = AlterImage;
                                                    localStorage.removeItem("profile");
                                                }}
                                            />
                                            <span className="profile-edit position-absolute" onClick={() => fileInputRef.current?.click()}> 
                                                <img src={PencilSimpleRoundedOutlineIcon} alt="ProfileAvatarIcon" width={32} height={32} />
                                                <label htmlFor="upload-input" className='upload-input-label' style={{ cursor: 'pointer' }}>
                                                    <input
                                                        id="upload-input"
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={handleFileChange}
                                                        ref={fileInputRef}
                                                    />
                                                </label>
                                            </span>
                                            {imageSrc != null && (<span className="profile-delete position-absolute cursor-pointer" onClick={removePicConfirmation}>
                                                    <img src={TrashRoundedOutlineIcon} alt="ProfileAvatarIcon" width={32} height={32} />
                                            </span>)}
                                        </span>
                                        <span className="profile-name mt-2 fs-5 fw-semibold">
                                            {formData.firstName} {formData.lastName}
                                        </span>
                                    </div>
                                    <div className="profile-menu rounded-3 p-2 cursor-pointer">
                                        <div
                                            className={`list-item ${activeTab === "edit" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("edit"); handleEditDetail(); }}
                                        >
                                            <PersonOutlineIcon />
                                            Edit Profile
                                        </div>

                                        <div
                                            className={`list-item ${activeTab === "password" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("password"); showChangePassword(); }}
                                        >
                                            <LockOutlinedIcon />
                                            Change Password
                                        </div>

                                        <div
                                            className={`list-item ${activeTab === "recent" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("recent"); showRecentLoginActivity(); }}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12.75 6.75V10.7859L16.1644 9.07875C16.3423 8.98974 16.5483 8.97504 16.737 9.0379C16.9257 9.10075 17.0818 9.23601 17.1708 9.41391C17.2598 9.59181 17.2745 9.79779 17.2116 9.98653C17.1488 10.1753 17.0135 10.3313 16.8356 10.4203L12.3356 12.6703C12.2213 12.7275 12.0943 12.7546 11.9666 12.7489C11.8389 12.7432 11.7147 12.7049 11.606 12.6378C11.4972 12.5706 11.4074 12.4768 11.3451 12.3651C11.2828 12.2535 11.2501 12.1278 11.25 12V6.75C11.25 6.55109 11.329 6.36033 11.4697 6.21967C11.6103 6.07902 11.8011 6 12 6C12.1989 6 12.3897 6.07902 12.5303 6.21967C12.671 6.36033 12.75 6.55109 12.75 6.75ZM12 20.25C10.3683 20.25 8.77326 19.7661 7.41655 18.8596C6.05984 17.9531 5.00242 16.6646 4.378 15.1571C3.75358 13.6497 3.5902 11.9909 3.90853 10.3905C4.22685 8.79017 5.01259 7.32016 6.16637 6.16637C7.32016 5.01259 8.79017 4.22685 10.3905 3.90853C11.9909 3.5902 13.6497 3.75358 15.1571 4.378C16.6646 5.00242 17.9531 6.05984 18.8596 7.41655C19.7661 8.77326 20.25 10.3683 20.25 12C20.25 12.1989 20.329 12.3897 20.4697 12.5303C20.6103 12.671 20.8011 12.75 21 12.75C21.1989 12.75 21.3897 12.671 21.5303 12.5303C21.671 12.3897 21.75 12.1989 21.75 12C21.75 10.0716 21.1782 8.18657 20.1068 6.58319C19.0355 4.97982 17.5127 3.73013 15.7312 2.99218C13.9496 2.25422 11.9892 2.06114 10.0979 2.43735C8.20656 2.81355 6.46928 3.74215 5.10571 5.10571C3.74215 6.46928 2.81355 8.20656 2.43735 10.0979C2.06114 11.9892 2.25422 13.9496 2.99218 15.7312C3.73013 17.5127 4.97982 19.0355 6.58319 20.1068C8.18657 21.1782 10.0716 21.75 12 21.75C12.1989 21.75 12.3897 21.671 12.5303 21.5303C12.671 21.3897 12.75 21.1989 12.75 21C12.75 20.8011 12.671 20.6103 12.5303 20.4697C12.3897 20.329 12.1989 20.25 12 20.25ZM21.7247 20.8069C21.7522 20.9027 21.7603 21.0031 21.7485 21.1021C21.7367 21.2011 21.7053 21.2968 21.6561 21.3836C21.607 21.4703 21.541 21.5464 21.462 21.6073C21.3831 21.6682 21.2928 21.7128 21.1964 21.7384C21.1001 21.764 20.9996 21.7702 20.9008 21.7565C20.802 21.7428 20.707 21.7096 20.6212 21.6587C20.5354 21.6078 20.4607 21.5404 20.4013 21.4603C20.3419 21.3802 20.2991 21.289 20.2753 21.1922C20.01 20.1956 19.0744 19.5 18 19.5C16.9256 19.5 15.99 20.1956 15.7247 21.1922C15.6823 21.3521 15.5883 21.4934 15.4572 21.5943C15.3261 21.6952 15.1654 21.7499 15 21.75C14.9348 21.7498 14.8699 21.7413 14.8069 21.7247C14.6147 21.6735 14.4507 21.548 14.351 21.3759C14.2513 21.2037 14.2241 20.9991 14.2753 20.8069C14.5186 19.9036 15.0907 19.1238 15.8794 18.6206C15.4595 18.2012 15.1734 17.6667 15.0574 17.0846C14.9414 16.5026 15.0006 15.8992 15.2276 15.3509C15.4545 14.8025 15.8391 14.3338 16.3325 14.004C16.8259 13.6742 17.4061 13.4981 17.9995 13.4981C18.593 13.4981 19.1732 13.6742 19.6666 14.004C20.16 14.3338 20.5445 14.8025 20.7715 15.3509C20.9985 15.8992 21.0577 16.5026 20.9417 17.0846C20.8256 17.6667 20.5396 18.2012 20.1197 18.6206C20.9087 19.1236 21.4812 19.9034 21.7247 20.8069ZM16.5 16.5C16.5 16.7967 16.588 17.0867 16.7528 17.3334C16.9176 17.58 17.1519 17.7723 17.426 17.8858C17.7001 17.9994 18.0017 18.0291 18.2926 17.9712C18.5836 17.9133 18.8509 17.7704 19.0607 17.5607C19.2704 17.3509 19.4133 17.0836 19.4712 16.7926C19.5291 16.5017 19.4994 16.2001 19.3858 15.926C19.2723 15.6519 19.08 15.4176 18.8334 15.2528C18.5867 15.088 18.2967 15 18 15C17.6022 15 17.2206 15.158 16.9393 15.4393C16.658 15.7206 16.5 16.1022 16.5 16.5Z" fill="black"/>
                                            </svg>


                                            Recent Login Activity
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-9">

                                {(!isEditDetail && !isChangePassword && !isRecentLoginActivity) && (<div className="row m-0">
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Email
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.email}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Phone Number
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.phoneNumber}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Birthdate
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.birthdate}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Company Name
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.companyName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Zip Code
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.zipcode}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Street Address
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.streetAddress}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Broker
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.isBroker? "Yes" : "No"}
                                            </div>
                                        </div>
                                    </div>

                                    {formData.isCompanyAccount && (<div className="col-sm-6">
                                        <div className="details-card py-3 h-100">
                                            <div className="title position-relative pb-1 mb-3">
                                                Company Account
                                                <div className="ul-title d-inline-block position-absolute top-100 start-0"></div>
                                            </div>
                                            <div className="desc">
                                                {formData.isCompanyAccount? "Yes" : "No"}
                                            </div>
                                        </div>
                                    </div>)}
                                </div>)}

                                {(isEditDetail && !isChangePassword && !isRecentLoginActivity) && (<form className="form__inner row d-flex mx-auto py-lg-5" onSubmit={onSubmit} noValidate>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="firstName" className="form-label">
                                            First Name <span className="star-required text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            placeholder="Enter first name"
                                            value={formData.firstName}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^A-Za-z ]/g, '');
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value: value
                                                    }
                                                });
                                            }}
                                            onBlur={handleBlur}
                                            className={`form-control ${errors.firstName ? "is_invalid" : ""
                                                }`}
                                        />
                                        {errors.firstName && (
                                            <div className="error-message">{errors.firstName}</div>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="lastName" className="form-label">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="lastName"
                                            name="lastName"
                                            placeholder="Enter last name"
                                            value={formData.lastName}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^A-Za-z ]/g, '');
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value: value
                                                    }
                                                });
                                            }}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="lastName" className="form-label">
                                            Email <span className="star-required text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="Enter your email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`form-control ${errors.email ? "is_invalid" : ""
                                                }`}
                                        />
                                        {errors.email && (
                                            <div className="error-message">{errors.email}</div>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="phoneNumber" className="form-label">
                                            Phone Number
                                        </label>
                                        <InputMask
                                            mask="(999) 999-9999"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                        >
                                            {(inputProps) => (
                                                <input
                                                    type="text"
                                                    id="phoneNumber"
                                                    name="phoneNumber"
                                                    placeholder="Enter phone number"
                                                    className="form-control"
                                                    {...inputProps}
                                                />
                                            )}
                                        </InputMask>
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="birthdate" className="form-label">
                                            Birthdate (Must be 21+ years) <span className="star-required text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="birthdate"
                                            name="birthdate"
                                            placeholder="Birthdate"
                                            value={formData.birthdate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            max={minAgeDate}
                                            // ref={dateInputRef}
                                            onClick={handleDateClick}
                                            className={`form-control ${errors.birthdate ? "is_invalid" : ""
                                                }`}
                                        />
                                        {errors.birthdate && (
                                            <div className="error-message">{errors.birthdate}</div>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="companyName" className="form-label">
                                            Company Name <span className="star-required text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="companyName"
                                            name="companyName"
                                            placeholder="Enter Company Name"
                                            value={formData.companyName}
                                            disabled = {true}
                                            // onChange={(e) => {
                                            //     const value = e.target.value.replace(/[^A-Za-z ]/g, '');
                                            //     handleChange({
                                            //         target: {
                                            //             name: e.target.name,
                                            //             value: value
                                            //         }
                                            //     });
                                            // }}
                                            // onBlur={handleBlur}
                                            className={`form-control ${errors.companyName ? "is_invalid" : ""
                                                }`}
                                        />
                                        {errors.companyName && (
                                            <div className="error-message">{errors.companyName}</div>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="zipcode" className="form-label">
                                            Zip Code <span className="star-required text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="zipcode"
                                            name="zipcode"
                                            placeholder="Enter Zip Code"
                                            value={formData.zipcode}
                                            onChange={handleChange}
                                            onBlur={handleZipcodeBlur}
                                            className={`form-control ${errors.zipcode ? "is_invalid" : ""
                                                }`}
                                        />
                                        {errors.zipcode && (
                                            <div className="error-message">{errors.zipcode}</div>
                                        )}
                                        {formData.city && formData.state && (
                                            <p className="mt-2 m-0">
                                                {formData.city + ", " + formData.state}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                                        <label htmlFor="companyName" className="form-label">
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            id="streetAddress"
                                            name="streetAddress"
                                            placeholder="Enter street address"
                                            value={formData.streetAddress}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 mt-3 mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="barrelBroker"
                                                name="barrelBroker"
                                                style={{ cursor: 'pointer' }}
                                                checked= {formData.isBroker}
                                                onChange={handleIsBrokerChange}
                                            />
                                            <label className="form-check-label" htmlFor="barrelBroker">
                                                I am a Broker
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <hr />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <div className="form__btn--group d-flex justify-content-center">
                                            <div className="pe-1">
                                                <button
                                                    type="reset"
                                                    className="btn secondary-btn register-btn mb-2"
                                                    onClick={handleViewDetail}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div className="ps-1">
                                                <button
                                                    type="submit"
                                                    className="btn primary-btn register-btn mb-2"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading && <Spinner />} Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>)}

                                {(isChangePassword && !isEditDetail && !isRecentLoginActivity) && (<ChangePassword onClose={hideChangePassword} email={formData.email}/>)}

                                {isRecentLoginActivity && !isEditDetail && !isChangePassword && (
                                    <div className="mt-4">
                                        <>
                                            <h2 className="tab-heading">Recent Login Activity</h2>
                                            {!isLoading && tableDataWithIds.length !== 0 && (
                                                <div className="login-history-grid-wrapper">
                                            <DataGrid
                                                disableRowSelectionOnClick
                                                rows={tableDataWithIds}
                                                columns={columns}
                                                getRowId={(row) => row.loginTime + row.email}
                                                initialState={{
                                                    pagination: {
                                                        paginationModel: { page, pageSize },
                                                    },
                                                }}
                                                className="data-grid login-history-grid"
                                                pagination
                                                paginationMode="server"
                                                rowCount={totalCount}
                                                pageSizeOptions={[10, 20, 50, 100]}
                                                loading={isLoading}
                                                slots={{
                                                    loadingOverlay: DataGridLoader,
                                                }}
                                                paginationModel={{
                                                    page,
                                                    pageSize,
                                                }}
                                                onPaginationModelChange={(newModel) => {
                                                    setPage(newModel.page);
                                                    setPageSize(newModel.pageSize);
                                                    getRecentLoginActivityData(newModel.page, newModel.pageSize);
                                                }}
                                            />
                                            </div>
                                            )}
                                            {!isLoading && tableDataWithIds.length === 0 && (
                                                <div className="no-data-wrapper flex-column">
                                                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M74.375 39.375V62.918L94.2922 52.9594C95.33 52.4402 96.5315 52.3544 97.6325 52.7211C98.7334 53.0877 99.6437 53.8767 100.163 54.9145C100.682 55.9522 100.768 57.1538 100.401 58.2547C100.035 59.3557 99.2456 60.2659 98.2078 60.7852L71.9578 73.9102C71.291 74.2439 70.5499 74.4016 69.805 74.3683C69.0601 74.3351 68.336 74.112 67.7015 73.7203C67.0671 73.3285 66.5433 72.7811 66.1798 72.13C65.8164 71.4789 65.6254 70.7457 65.625 70V39.375C65.625 38.2147 66.086 37.1019 66.9064 36.2814C67.7269 35.461 68.8397 35 70 35C71.1603 35 72.2731 35.461 73.0936 36.2814C73.9141 37.1019 74.375 38.2147 74.375 39.375ZM70 118.125C60.4818 118.125 51.1773 115.303 43.2632 110.015C35.3491 104.726 29.1808 97.2104 25.5383 88.4167C21.8959 79.623 20.9428 69.9466 22.7997 60.6113C24.6566 51.276 29.2401 42.7009 35.9705 35.9705C42.7009 29.2401 51.276 24.6566 60.6113 22.7997C69.9466 20.9428 79.623 21.8959 88.4167 25.5383C97.2104 29.1808 104.726 35.3491 110.015 43.2632C115.303 51.1773 118.125 60.4818 118.125 70C118.125 71.1603 118.586 72.2731 119.406 73.0936C120.227 73.9141 121.34 74.375 122.5 74.375C123.66 74.375 124.773 73.9141 125.594 73.0936C126.414 72.2731 126.875 71.1603 126.875 70C126.875 58.7512 123.539 47.755 117.29 38.402C111.04 29.0489 102.158 21.7591 91.7651 17.4544C81.3726 13.1496 69.9369 12.0233 58.9043 14.2179C47.8716 16.4124 37.7374 21.8292 29.7833 29.7833C21.8292 37.7374 16.4124 47.8716 14.2179 58.9043C12.0233 69.9369 13.1496 81.3726 17.4544 91.7651C21.7591 102.158 29.0489 111.04 38.402 117.29C47.755 123.539 58.7512 126.875 70 126.875C71.1603 126.875 72.2731 126.414 73.0936 125.594C73.9141 124.773 74.375 123.66 74.375 122.5C74.375 121.34 73.9141 120.227 73.0936 119.406C72.2731 118.586 71.1603 118.125 70 118.125ZM126.727 121.373C126.888 121.933 126.935 122.518 126.866 123.096C126.798 123.673 126.614 124.231 126.327 124.737C126.041 125.243 125.656 125.687 125.195 126.043C124.735 126.398 124.208 126.658 123.646 126.807C123.084 126.957 122.497 126.993 121.921 126.913C121.345 126.833 120.791 126.639 120.29 126.342C119.79 126.046 119.354 125.652 119.007 125.185C118.661 124.718 118.411 124.186 118.273 123.621C116.725 117.808 111.267 113.75 105 113.75C98.7328 113.75 93.275 117.808 91.7274 123.621C91.4801 124.554 90.9316 125.378 90.167 125.967C89.4025 126.555 88.4648 126.875 87.5 126.875C87.1197 126.874 86.7411 126.824 86.3735 126.727C85.2523 126.429 84.2958 125.697 83.7143 124.693C83.1328 123.689 82.9739 122.495 83.2727 121.373C84.6918 116.104 88.0291 111.555 92.6297 108.62C90.1804 106.174 88.5117 103.056 87.835 99.6603C87.1582 96.2651 87.5036 92.7455 88.8276 89.5467C90.1515 86.3478 92.3945 83.6136 95.2728 81.6898C98.1511 79.766 101.535 78.7392 104.997 78.7392C108.459 78.7392 111.843 79.766 114.722 81.6898C117.6 83.6136 119.843 86.3478 121.167 89.5467C122.491 92.7455 122.836 96.2651 122.16 99.6603C121.483 103.056 119.814 106.174 117.365 108.62C121.967 111.554 125.307 116.103 126.727 121.373ZM96.25 96.25C96.25 97.9806 96.7632 99.6723 97.7247 101.111C98.6861 102.55 100.053 103.672 101.652 104.334C103.25 104.996 105.01 105.17 106.707 104.832C108.404 104.494 109.964 103.661 111.187 102.437C112.411 101.214 113.244 99.6544 113.582 97.9571C113.92 96.2597 113.746 94.5004 113.084 92.9015C112.422 91.3027 111.3 89.9361 109.861 88.9747C108.422 88.0132 106.731 87.5 105 87.5C102.679 87.5 100.454 88.4219 98.8128 90.0628C97.1719 91.7038 96.25 93.9294 96.25 96.25Z" fill="#CACACA" />
                                                    </svg>
                                                    <p>This user has never logged in.</p>
                                                </div>
                                            )}
                                        </>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {selectedFile && (
                <ImageUploader file={selectedFile} onSave={base64ToFile} />
            )}
        </>
    )
}

export default ViewOrEditProfile;